import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "bar",
    "currentStep",
    "nextButton",
    "backButton",
    "submitButton",
    "form",
    "step",
    "reviewSubmit",
  ];
  static values = { scrollToTop: Boolean };

  connect() {
    this.element.addEventListener("progress:nextStep", this.nextStep.bind(this));
    this.element.addEventListener("progress:backStep", this.backStep.bind(this));
  }

  async nextStep() {
    const currentStep = parseInt(this.element.dataset.currentStep, 10);
    const oldBar = this.barTargets[currentStep - 1];
    const newBar = this.barTargets[currentStep];

    if (oldBar) {
      await this.fillBar(oldBar, 100);
    }

    if (newBar) {
      await this.fillBar(newBar, 10);
      this.element.dataset.currentStep = currentStep + 1;
    }

    this.toggleStepVisibility(currentStep + 1);

    this.updateButtonVisibility(currentStep + 1);

    this.scrollToTop();
  }

  async backStep() {
    const currentStep = parseInt(this.element.dataset.currentStep, 10);
    const oldBar = this.barTargets[currentStep - 1];
    const newBar = this.barTargets[currentStep - 2];

    if (oldBar) {
      await this.fillBar(oldBar, 0);
    }

    if (newBar) {
      await this.fillBar(newBar, 10);
    }

    this.toggleStepVisibility(currentStep - 1);

    this.updateButtonVisibility(currentStep - 1);

    this.element.dataset.currentStep = currentStep - 1;

    this.scrollToTop();
  }

  onNext(event) {
    const currentStep = parseInt(this.element.dataset.currentStep, 10);
    const currentStepElement = this.stepTargets[currentStep - 1];
    if (!this.validateFieldsIn(currentStepElement)) {
      event.preventDefault();
      return;
    }
    this.nextStep();

    const submitter = event.target.closest("button[type='submit']");
    if (!this.hasFormTarget && submitter?.form) {
      this.scrollToTopAfterSubmit(submitter.form);
      return;
    }

    event.preventDefault();
    this.submitOnNext();
  }

  onBack(event) {
    event.preventDefault();
    this.backStep();
  }

  submitOnNext() {
    if (
      this.hasFormTarget &&
      this.hasReviewSubmitTarget &&
      this.element.dataset.submitOnNext === "true"
    ) {
      this.scrollToTopAfterSubmit(this.formTarget);
      this.formTarget.requestSubmit(this.reviewSubmitTarget);
    }
  }

  scrollToTopAfterSubmit(form) {
    form.addEventListener("turbo:submit-end", () => setTimeout(() => this.scrollToTop(), 300), {
      once: true,
    });
  }

  validateFieldsIn(stepElement) {
    const fields = stepElement.querySelectorAll("input, select, textarea");
    let valid = true;

    fields.forEach((field) => {
      if (!field.checkValidity()) {
        field.reportValidity();
        valid = false;
      }
    });
    return valid;
  }

  fillBar(barElement, value) {
    return new Promise((resolve) => {
      if (!barElement) {
        return resolve();
      }

      barElement.setAttribute("aria-valuenow", value);

      const fillElement = barElement.children[0];
      fillElement.style.width = `${value}%`;

      const afterTransition = () => {
        resolve();
      };

      fillElement.addEventListener("transitionend", afterTransition);
    });
  }

  updateButtonVisibility(currentStep) {
    if (this.hasCurrentStepTarget) {
      this.currentStepTarget.textContent = currentStep;
    }

    // Back button visibility
    if (this.hasBackButtonTarget) {
      if (currentStep <= 1) {
        this.backButtonTarget.classList.add("hidden");
      } else {
        this.backButtonTarget.classList.remove("hidden");
      }
    }

    // Next and Submit button visibility
    if (this.hasNextButtonTarget) {
      if (currentStep >= this.barTargets.length) {
        this.nextButtonTarget.classList.add("hidden");
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.classList.remove("hidden");
        }
      } else {
        this.nextButtonTarget.classList.remove("hidden");
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.classList.add("hidden");
        }
      }
    }
  }

  toggleStepVisibility(currentStep) {
    if (this.hasStepTarget) {
      this.stepTargets.forEach((step, index) => {
        step.classList.toggle("hidden", index !== currentStep - 1);
      });
    }
  }

  scrollToTop() {
    if (!this.hasScrollToTopValue || !this.scrollToTopValue) return;

    if (!document.body.contains(this.element)) return;

    const currentStep = parseInt(this.element.dataset.currentStep, 10);
    const currentStepElement = this.stepTargets[currentStep - 1];

    if (!currentStepElement) return;

    currentStepElement.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}
